import {
  usePostUpsertAnnotation,
  useGetAnnotationByPizzaId,
} from "@/api/pizza-checker/pizza-annotations/pizza-annotations";
import { PostUpsertAnnotationBody } from "@/api/pizza-checker/schemas";
import { useEffect, useState, useCallback } from "react";
import { usePizzaEvaluationContext } from "@/context/pizza-evaluation-context";
import { Keyboard } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";

type AnnotationCategory = "Raw" | "Burnt" | "Components";
type AnnotationValue = "OK" | "NOK" | "None";
type ComparisonStatus = "match" | "mismatch" | "missing" | "no-evaluation";

// `label` / `title` are translation keys; the hotkeys stay the same in every language
const CATEGORIES: { key: AnnotationCategory; label: TranslationKey; hotkey: string }[] = [
  { key: "Burnt", label: "annotation.category.burnt", hotkey: "B" },
  { key: "Raw", label: "annotation.category.raw", hotkey: "R" },
  { key: "Components", label: "annotation.category.components", hotkey: "C" },
];

const VALUES: { value: AnnotationValue; label: TranslationKey | null; active: string }[] = [
  { value: "OK", label: "status.ok", active: "bg-green-500/20 text-green-300 ring-1 ring-inset ring-green-500/50" },
  { value: "NOK", label: "status.nok", active: "bg-red-500/20 text-red-300 ring-1 ring-inset ring-red-500/50" },
  { value: "None", label: null, active: "bg-muted text-foreground ring-1 ring-inset ring-border" },
];

// agreement with the evaluation: only a mismatch is loud, an unannotated row is just an empty ring
const INDICATORS: Record<ComparisonStatus, { className: string; text: string; title: TranslationKey }> = {
  match: { className: "bg-green-500/15 text-green-400", text: "✓", title: "annotation.match" },
  mismatch: { className: "bg-red-500/80 text-white", text: "✗", title: "annotation.mismatch" },
  missing: { className: "ring-1 ring-inset ring-muted-foreground/40 text-transparent", text: "", title: "annotation.missing" },
  "no-evaluation": { className: "text-muted-foreground/60", text: "–", title: "annotation.noEvaluation" },
};

/**
 * Quality annotation of the open pizza (Burnt / Raw / Components as OK / NOK), saved on every change and
 * compared with the evaluation. Hotkeys: B, R, C pick the row, then O or N sets it.
 */
export function PizzaAnnotationPanel({ className }: { className?: string }) {
  const { t } = useLocalization();
  const { pizza, metadata } = usePizzaEvaluationContext();
  const { mutate: upsertAnnotation } = usePostUpsertAnnotation();

  // Fetch existing annotation data for the current pizza
  const { data: existingAnnotations } = useGetAnnotationByPizzaId(
    pizza?.id as string,
    {
      query: {
        enabled: !!pizza?.id,
      },
    },
  );

  const [annotations, setAnnotations] = useState<
    Omit<PostUpsertAnnotationBody, "pizza_id">
  >({
    Raw: "None",
    Burnt: "None",
    Components: "None",
  });
  const [focusedAnnotation, setFocusedAnnotation] =
    useState<AnnotationCategory | null>(null);

  // Update annotations when existing data is loaded or pizza changes
  useEffect(() => {
    if (existingAnnotations) {
      const initialAnnotations: Omit<PostUpsertAnnotationBody, "pizza_id"> = {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Raw: (existingAnnotations as any)?.Raw || "None",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Burnt: (existingAnnotations as any)?.Burnt || "None",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Components: (existingAnnotations as any)?.Components || "None",
      };
      setAnnotations(initialAnnotations);
    } else if (pizza && "annotations" in pizza && pizza.annotations) {
      // Fallback to pizza.annotations if exists (for PizzaDTO type)
      const initialAnnotations: Omit<PostUpsertAnnotationBody, "pizza_id"> = {};
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const annotations = (pizza as any).annotations;
      if (annotations.Raw) initialAnnotations.Raw = annotations.Raw;
      if (annotations.Burnt) initialAnnotations.Burnt = annotations.Burnt;
      if (annotations.Components)
        initialAnnotations.Components = annotations.Components;
      setAnnotations(initialAnnotations);
    } else {
      setAnnotations({ Raw: "None", Burnt: "None", Components: "None" });
    }
  }, [existingAnnotations, pizza]);

  // Function to compare annotation with evaluation data
  const getComparisonStatus = useCallback(
    (category: AnnotationCategory): ComparisonStatus => {
      const annotationValue = annotations[category];

      if (!metadata) {
        return "no-evaluation";
      }

      if (!annotationValue || annotationValue === "None") {
        return "missing";
      }

      switch (category) {
        case "Burnt": {
          const classification = metadata.classification;
          if (annotationValue === "OK") {
            // Annotation says "OK" (not burnt) - should match if classification is not "Burnt Pizza"
            return classification !== "Burnt Pizza" ? "match" : "mismatch";
          } else if (annotationValue === "NOK") {
            // Annotation says "NOK" (burnt) - should match if classification is "Burnt Pizza"
            return classification === "Burnt Pizza" ? "match" : "mismatch";
          }
          break;
        }

        case "Raw": {
          // Single-output crust-strip CNN (v1): raw/uncooked is not evaluated at all.
          // Two-output CNN: doneness_evaluation.raw carries the model's verdict.
          // Legacy evaluations carried a rule-based per-quarter estimate.
          const rawEval = metadata.doneness_evaluation?.raw;
          if (rawEval?.evaluated === false) {
            return "no-evaluation";
          }
          if (rawEval?.evaluated === true) {
            const isRawByModel = rawEval.status === true;
            if (annotationValue === "OK") {
              return !isRawByModel ? "match" : "mismatch";
            } else if (annotationValue === "NOK") {
              return isRawByModel ? "match" : "mismatch";
            }
            break;
          }
          const quarterResults = metadata.quarter_results || [];
          if (quarterResults.length === 0) {
            return "no-evaluation";
          }

          const avgRawLevel =
            quarterResults.reduce((sum, q) => sum + (q.level0 || 0), 0) /
            quarterResults.length;
          const isRawByEvaluation =
            avgRawLevel > 0.15 || metadata.classification === "Uncooked Pizza";

          if (annotationValue === "OK") {
            // Annotation says "OK" (not raw) - should match if evaluation shows it's not raw
            return !isRawByEvaluation ? "match" : "mismatch";
          } else if (annotationValue === "NOK") {
            // Annotation says "NOK" (raw) - should match if evaluation shows it's raw
            return isRawByEvaluation ? "match" : "mismatch";
          }
          break;
        }

        case "Components": {
          const detectedComponents = metadata.components_bbs || [];
          const hasComponents = detectedComponents.length > 0;

          if (annotationValue === "OK") {
            // Annotation says "OK" (components are fine) - should match if components are detected
            return hasComponents ? "match" : "mismatch";
          } else if (annotationValue === "NOK") {
            // Annotation says "NOK" (components are missing/bad) - should match if no components or very few
            return !hasComponents || detectedComponents.length < 2
              ? "match"
              : "mismatch";
          }
          break;
        }
      }

      return "no-evaluation";
    },
    [annotations, metadata],
  );

  const handleAnnotationChange = useCallback(
    (category: AnnotationCategory, value: AnnotationValue) => {
      if (!pizza) return;

      const newAnnotations = { ...annotations, [category]: value };
      setAnnotations(newAnnotations);

      upsertAnnotation({
        data: {
          pizza_id: pizza.id,
          ...newAnnotations,
        },
      });
    },
    [annotations, pizza, upsertAnnotation],
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.ctrlKey ||
        event.metaKey ||
        event.altKey ||
        (event.target instanceof Element &&
          event.target.closest("input, textarea, select, [contenteditable=true]"))
      ) {
        return;
      }

      const key = event.key.toUpperCase();
      if (key === "R") {
        setFocusedAnnotation("Raw");
      } else if (key === "B") {
        setFocusedAnnotation("Burnt");
      } else if (key === "C") {
        setFocusedAnnotation("Components");
      } else if (key === "ESCAPE") {
        setFocusedAnnotation(null);
      } else if (focusedAnnotation) {
        if (key === "O") {
          handleAnnotationChange(focusedAnnotation, "OK");
        } else if (key === "N") {
          handleAnnotationChange(focusedAnnotation, "NOK");
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [focusedAnnotation, handleAnnotationChange]);

  return (
    <section className={cn("rounded-xl border bg-card", className)} aria-label={t("annotation.title")}>
      <div className="flex items-center justify-between gap-2 px-4 pb-1 pt-3">
        <h3 className="text-sm font-semibold">{t("annotation.title")}</h3>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="hidden items-center gap-1 rounded-md px-1.5 py-1 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground md:flex"
            >
              <Keyboard className="h-3.5 w-3.5" />
              {t("annotation.hotkeysShort")}
            </button>
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-56">
            {t("annotation.hotkeysHelp")}
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="flex flex-col gap-1 px-2 pb-2">
        {CATEGORIES.map(({ key, label, hotkey }) => {
          const indicator = INDICATORS[getComparisonStatus(key)];
          const value = (annotations[key] ?? "None") as AnnotationValue;
          return (
            <div
              key={key}
              className={cn(
                "flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors",
                focusedAnnotation === key && "bg-primary/10 ring-1 ring-inset ring-primary/40",
              )}
            >
              <button
                type="button"
                onClick={() => setFocusedAnnotation(focusedAnnotation === key ? null : key)}
                className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm"
              >
                <kbd className="hidden h-5 min-w-5 items-center justify-center rounded border bg-muted px-1 text-[10px] font-semibold text-muted-foreground md:inline-flex">
                  {hotkey}
                </kbd>
                <span className="truncate font-medium">{t(label)}</span>
                <span
                  className={cn(
                    "flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold",
                    indicator.className,
                  )}
                  title={t(indicator.title)}
                >
                  {indicator.text}
                </span>
              </button>
              <div
                role="radiogroup"
                aria-label={t(label)}
                className="flex shrink-0 rounded-md bg-muted/60 p-0.5"
              >
                {VALUES.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={value === option.value}
                    disabled={!pizza}
                    onClick={() => handleAnnotationChange(key, option.value)}
                    className={cn(
                      "h-8 min-w-11 rounded px-2 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50 md:h-7 md:min-w-10",
                      value === option.value && option.active,
                    )}
                  >
                    {option.label ? t(option.label) : "–"}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
